/**
 * @file Handles date-related operations.
 * */
import { hyphenatedCurrentLocale as locale } from 'bits/current_locale'

/**
 * Calculates the time difference between two dates in days.
 *
 * @param earlierDate Should be earlier than laterDate
 * @param laterDate Should be later than earlierDate
 * @param round Set to 'down' by default, can be set to 'up' and 'none'
 * @returns The day difference in milliseconds
 */
function dayDifference(earlierDate: Date, laterDate: Date, round = 'down'): number {
  const differenceInDays = (laterDate.getTime() - earlierDate.getTime()) / (1000 * 3600 * 24)

  if (round === 'down') {
    return Math.floor(differenceInDays)
  } else if (round === 'up') {
    return Math.ceil(differenceInDays)
  } else {
    return differenceInDays
  }
}

/**
 * Adds the days specified to the date provided and return the resultant date.
 * Can supply a negative number for days to minus instead.
 *
 * @param date The date to add days to
 * @param days The days to add to the date supplied
 * @returns The new date after the days are added
 */
function addDays(date: Date, days: number): Date {
  const newDate = new Date(date)
  newDate.setDate(date.getDate() + days)
  return newDate
}

/**
 * Returns a pretty version of the current date.
 * E.g "October 21, 2015"
 *
 * @returns a pretty version of the current date.
 */
function getPrettyCurrentDate(): string {
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  } as const
  return new Intl.DateTimeFormat(locale, options).format(new Date())
}

export { dayDifference, addDays, getPrettyCurrentDate }
